import { useState } from "react";
import { Link } from "react-router-dom";
import PageMeta from "@/components/PageMeta";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { IMAGES, ORG, PAYMENTS } from "@/lib/constants";
import {
  ArrowUpRight,
  CreditCard,
  Smartphone,
  Banknote,
  ShieldCheck,
  HeartHandshake,
  Flame,
  Building2,
  GraduationCap,
  Users,
  Backpack,
  Sparkles,
  Copy as CopyIcon,
  Check,
} from "lucide-react";

// /donate-sponsor is the evergreen general-support page for the Phoenix of
// the Detroit Fire Department. It focuses on year-round Phoenix work:
// Historic Engine 11 preservation, Project 100, community outreach, families
// impacted by fire, senior feeding and fellowship, the annual backpack
// giveaway, retiree fellowship, and active member and family fellowship.
//
// 2027 Conference + 60th Anniversary sponsorship tiers, ad journal, table
// sponsorship, gala-specific sponsorship language, and event-specific
// volunteer CTAs DO NOT live on this page. They live on /2027.  A small
// directional bridge on this page links visitors over.

const SUPPORT_AREAS = [
  {
    icon: Building2,
    t: "Historic Engine 11 Preservation",
    b: "Roof, masonry, mechanical, accessibility, and ongoing building improvements at 2733 Gratiot Avenue — Detroit's oldest active-use firehouse.",
  },
  {
    icon: GraduationCap,
    t: "Project 100",
    b: "Mentorship, prep, and the path to the Detroit Fire Department for the next generation of Phoenix members.",
  },
  {
    icon: HeartHandshake,
    t: "Community Outreach",
    b: "Year-round outreach across Detroit — community days, school visits, and Phoenix presence where it counts.",
  },
  {
    icon: Flame,
    t: "Families Impacted by Fire",
    b: "Direct support for Detroit families and households impacted by fire and emergency events.",
  },
  {
    icon: Users,
    t: "Senior Feeding & Fellowship",
    b: "Meals, programs, and fellowship for Detroit seniors hosted through Phoenix.",
  },
  {
    icon: Backpack,
    t: "Annual Backpack Giveaway",
    b: "Each August, Phoenix supports Detroit students and families through the annual backpack and school-supply giveaway.",
  },
  {
    icon: Sparkles,
    t: "Retiree Fellowship",
    b: "Monthly Retirees Breakfast, Annual Retirees Luncheon, and ongoing retiree fellowship at Historic Engine 11.",
  },
  {
    icon: Users,
    t: "Active Member & Family Fellowship",
    b: "Phoenix programming that strengthens active Phoenix members and their families throughout the year.",
  },
];

function CopyButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        });
      }}
      className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-200 hover:border-amber-500/50 hover:text-amber-400 transition-colors"
      data-testid={testid}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <CopyIcon className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default function DonateSponsor() {
  return (
    <div data-testid="donate-page">
      <PageMeta
        title="Support Phoenix"
        description="Support Phoenix of the Detroit Fire Department — Historic Engine 11 preservation, Project 100, community outreach, families impacted by fire, senior feeding and fellowship, backpack giveaway, retiree fellowship, and active member and family fellowship."
        path="/donate-sponsor"
      />
      <PageHero
        overline="Support · General Donations"
        title="Stand with the Phoenix."
        subtitle="Every dollar supports year-round Phoenix work — Historic Engine 11 preservation, Project 100, community outreach, families impacted by fire, senior feeding and fellowship, the annual backpack giveaway, retiree fellowship, and active member and family fellowship. The Phoenix of the Detroit Fire Department is a 501(c)(3) nonprofit."
        image={IMAGES.detroit}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <a
            href="#ways-to-give"
            className="btn-gold w-full sm:w-auto justify-center"
            data-testid="hero-give"
          >
            Ways to Give <ArrowUpRight className="h-4 w-4" />
          </a>
          <Link
            to="/contact?type=donation"
            className="btn-primary w-full sm:w-auto justify-center"
            data-testid="hero-donation-inquiry"
          >
            Donation Inquiry
          </Link>
        </div>
      </PageHero>

      {/* ----- WHAT YOUR SUPPORT FUELS ----- */}
      <section className="py-12 lg:py-24" data-testid="support-areas">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="What Your Support Fuels"
            title="Year-round Phoenix work."
            subtitle="Phoenix shows up across Detroit every season of the year. General Phoenix support directly fuels the work below."
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SUPPORT_AREAS.map((s) => (
              <div key={s.t} className="card-dark p-6 flex flex-col" data-testid={`support-${s.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <s.icon className="h-7 w-7 text-amber-500" />
                <div className="font-display text-lg text-white mt-4 leading-tight">{s.t}</div>
                <p className="text-slate-400 mt-2 text-sm leading-relaxed flex-1">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- WAYS TO GIVE — payment tiles ----- */}
      <section id="ways-to-give" className="py-12 lg:py-24 bg-[#0E0E0E]" data-testid="ways-to-give">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Ways to Give"
            title="Direct support for Phoenix."
            subtitle="Choose the option that works for you. All three send funds directly to Phoenix for general support of year-round Phoenix work."
          />

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* ---- Cash App ---- */}
            <div className="card-dark p-6 flex flex-col" data-testid="payment-cashapp">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 flex-shrink-0">
                  <Smartphone className="h-5 w-5" />
                </span>
                <div className="font-display text-lg text-white">Cash App</div>
              </div>
              <div className="text-sm text-slate-400 mt-3 leading-relaxed">
                Send a direct general donation to Phoenix on Cash App.
              </div>
              <div className="mt-4 flex items-center gap-2 text-amber-400 font-mono text-sm">
                <span>$DetroitPhoenix</span>
                <CopyButton value="$DetroitPhoenix" testid="copy-cashapp" />
              </div>
              <div className="mt-auto pt-6">
                <a
                  href={PAYMENTS.cashapp.payUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold w-full justify-center text-sm"
                  data-testid="cashapp-pay-btn"
                >
                  Open Cash App <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* ---- PayPal ---- */}
            <div className="card-dark p-6 flex flex-col" data-testid="payment-paypal">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 flex-shrink-0">
                  <CreditCard className="h-5 w-5" />
                </span>
                <div className="font-display text-lg text-white">PayPal</div>
              </div>
              <div className="text-sm text-slate-400 mt-3 leading-relaxed">
                Use Phoenix&apos;s PayPal link to send a direct general donation, or scan the Phoenix PayPal QR from your phone.
              </div>
              <div className="mt-5 mx-auto rounded-xl bg-white p-3 ring-1 ring-amber-500/20 w-[160px] h-[160px] flex items-center justify-center" data-testid="paypal-qr-frame">
                <img
                  src={PAYMENTS.paypal.qrUrl}
                  alt="Phoenix PayPal QR code — scan to open the Phoenix DetroitPhoenix PayPal payment page."
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="mt-auto pt-6">
                <a
                  href={PAYMENTS.paypal.payUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold w-full justify-center text-sm"
                  data-testid="paypal-pay-btn"
                >
                  Pay with PayPal <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* ---- Zelle ---- */}
            <div className="card-dark p-6 flex flex-col" data-testid="payment-zelle">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 flex-shrink-0">
                  <Banknote className="h-5 w-5" />
                </span>
                <div className="font-display text-lg text-white">Zelle</div>
              </div>
              <div className="text-sm text-slate-400 mt-3 leading-relaxed">
                Send through your bank&apos;s Zelle to Phoenix&apos;s verified email, or scan the Zelle QR.
              </div>
              <div className="mt-4 flex items-center gap-2 text-amber-400 font-mono text-sm" data-testid="zelle-email">
                <span>{PAYMENTS.zelle.email}</span>
                <CopyButton value={PAYMENTS.zelle.email} testid="copy-zelle-email" />
              </div>
              <div className="mt-5 mx-auto rounded-xl bg-white p-3 ring-1 ring-amber-500/20 w-[160px] h-[160px] flex items-center justify-center" data-testid="zelle-qr-frame">
                <img
                  src={PAYMENTS.zelle.qrUrl}
                  alt="Phoenix Zelle QR code — scan to send a Zelle payment to 8phoenixrising@gmail.com."
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="mt-auto pt-6 text-xs text-slate-500 leading-relaxed">
                Confirm the recipient name shows as Phoenix before sending.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- 2027 BRIDGE — removed per evergreen rule. 2027-specific support now lives only on /2027. ----- */}

      {/* ----- 501(c)(3) NOTE + ADDRESS ----- */}
      <section className="py-16 lg:py-24" data-testid="donate-501c3">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="card-dark p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-2 flex justify-center">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
                <ShieldCheck className="h-7 w-7" />
              </span>
            </div>
            <div className="lg:col-span-10">
              <div className="overline">501(c)(3) Nonprofit</div>
              <h3 className="font-display text-xl lg:text-2xl text-white mt-2 leading-tight">
                The Phoenix of the Detroit Fire Department.
              </h3>
              <p className="text-slate-400 mt-3 text-sm leading-relaxed">
                Donations are tax-deductible to the extent allowed by law.
                Mailing address: {ORG.address}. For donor questions or to
                request a receipt, reach out through the Phoenix Contact page.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <Link to="/contact?type=donation" className="btn-gold w-full sm:w-auto justify-center text-sm" data-testid="donate-501c3-contact">
                  Donor Questions <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
